import { Button } from "@/components/ui/baseUi/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/baseUi/dropdown-menu"
import { ExportModal } from "@/modules/finances/components/exportModal"
import { BoxPromotionCodeCard } from "@/modules/finances/components/promotionCodeCard"
import { TransactionsMyWalletCard } from "@/modules/finances/components/transactionsMyWalletCard"
import { TransactionsResumeCard } from "@/modules/finances/components/transactionsResumeCard"
import { useDownloadAllMonthlyInvoices } from "@/modules/finances/hooks/useFinanceActions"
import { TransactionTableFilter } from "@/modules/finances/transactions/components/transactionTableFilter"
import { ApiFinanceType } from "@/modules/finances/types/financesType"
import { cn } from "@/modules/lib/utils"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import { StripeAccountErrorItem } from "@modules/finances/components/stripeAccountErrorAlert"
import { StripeErrorMissingDocumentButton } from "@modules/finances/components/stripeErrorButtons"
import { BoxTransactionsTable } from "@modules/finances/transactions/components/boxTransactionsTable"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { ChevronDownIcon, DownloadIcon } from "lucide-react"
import { Suspense, useState } from "react"

export type BoxPayinLayoutProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxPayinLayout({ className, ...props }: BoxPayinLayoutProps) {
  const [displayModal, setDisplayModal] = useState(false)
  const { result: box } = useCurrentBox()
  const { downloadAllMonthlyInvoicesMutation } = useDownloadAllMonthlyInvoices()

  const onDownloadAllMonthlyInvoices = () => {
    downloadAllMonthlyInvoicesMutation.mutateAsync()
  }

  if (!box) return null

  return (
    <div className={cn("flex min-w-250 grow flex-col gap-4", className)} {...props}>
      <div className="flex flex-row gap-4">
        <TransactionsResumeCard />
        <TransactionsMyWalletCard />
        <BoxPromotionCodeCard />
      </div>

      {box.accountError && (
        <StripeAccountErrorItem
          orientation="horizontal"
          alertTitle={word("global.stripeError.missingDocument.title")}
          alertContent={word("global.stripeError.missingDocument.content")}
          primaryButton={<StripeErrorMissingDocumentButton accountErrorCode={box.accountError} openBankingTab={true} />}
        />
      )}

      <div className="flex flex-row flex-wrap items-center justify-between gap-2 md:gap-1 xl:flex-row">
        <TransactionTableFilter />

        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button variant="secondary" size="md" disabled={downloadAllMonthlyInvoicesMutation.isPending}>
                <DownloadIcon color="currentColor" size={16} />
                {word("global.download")}
                <ChevronDownIcon color="currentColor" size={16} />
                {downloadAllMonthlyInvoicesMutation.isPending && <LoadingSpinner />}
              </Button>
            }
          />
          <DropdownMenuContent className="mr-4 w-fit">
            <DropdownMenuItem onClick={() => void onDownloadAllMonthlyInvoices()}>
              {word("finances.page.export.allInvoices")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setDisplayModal(true)}>
              {word("finances.page.export.listOfTransactions")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Suspense fallback={<LoadingSpinner />}>
        <BoxTransactionsTable />
      </Suspense>

      <ExportModal onHide={() => setDisplayModal(false)} visible={displayModal} financeApiType={ApiFinanceType.BOX} />
    </div>
  )
}
